import React, { useCallback, useEffect, useMemo, useState } from "react";
import { mailMetrikleriniGetir, testMailGonder } from "../services/mailSystemService";
import "../styles/emailSystemAdmin.css";

const RANGE_OPTIONS = [1, 7, 30];

function formatDay(value, lang) {
  const date = new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "tr-TR", {
    day: "2-digit",
    month: "short",
  }).format(date);
}

export default function EmailSystemAdmin({ t, lang, defaultEmail = "" }) {
  const [days, setDays] = useState(1);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [target, setTarget] = useState("");
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setMetrics(await mailMetrikleriniGetir(days));
    } catch (requestError) {
      setError(requestError?.message || t("mgmt.mail.metricsFailed"));
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    load();
  }, [load]);

  const chart = useMemo(() => {
    const brevo = Number(metrics?.totals?.brevo || 0);
    const resend = Number(metrics?.totals?.resend || 0);
    const total = brevo + resend;
    let offset = 0;
    return {
      total,
      slices: [
        { key: "brevo", label: "Brevo", value: brevo, color: "#765cf4" },
        { key: "resend", label: "Resend", value: resend, color: "#32b5d8" },
      ].map((slice) => {
        const percent = total ? (slice.value / total) * 100 : 0;
        const next = { ...slice, percent, offset };
        offset += percent;
        return next;
      }),
    };
  }, [metrics]);

  const sendTest = async () => {
    try {
      setSending(true);
      setTestResult(null);
      const response = await testMailGonder({ to: target.trim() || defaultEmail, lang });
      setTestResult({ ok: true, ...response });
      await load();
    } catch (requestError) {
      setTestResult({ ok: false, message: requestError?.message || t("mgmt.mail.testFailed") });
    } finally {
      setSending(false);
    }
  };

  const today = metrics?.today || {};
  const brevoRemaining = Math.max(0, Number(metrics?.limits?.brevo || 0) - Number(today.brevo || 0));
  const resendRemaining = Math.max(0, Number(metrics?.limits?.resend || 0) - Number(today.resend || 0));
  const primaryProvider = metrics?.primaryProvider || "brevo";
  const primaryState = (metrics?.providers || []).find((item) => item.provider === primaryProvider);
  const circuitOpen = primaryState?.circuit_open_until && new Date(primaryState.circuit_open_until).getTime() > Date.now();
  const failureSummary = metrics?.failures?.selected || {};
  const failedAttempts = Number(failureSummary.providerAttempts ?? metrics?.totals?.failed ?? 0);
  const recoveredFailures = Number(failureSummary.recovered || 0);
  const undeliveredFailures = Number(failureSummary.undelivered || 0);
  const selectedSlice = chart.slices.find((slice) => slice.key === selectedProvider);

  const failureCategory = (category) => ({
    performance_alert: t("mgmt.mail.categoryPerformance"),
    password_reset: t("mgmt.mail.categoryPasswordReset"),
    email_verification: t("mgmt.mail.categoryVerification"),
    admin_test: t("mgmt.mail.categoryAdminTest"),
    system: t("mgmt.mail.categorySystem"),
  }[category] || category || t("mgmt.mail.categorySystem"));

  return (
    <div className="emailSystemAdmin">
      <div className="emailSystemToolbar">
        <div className="emailSystemRanges" role="group" aria-label={t("mgmt.mail.rangeLabel")}>
          {RANGE_OPTIONS.map((range) => (
            <button
              type="button"
              key={range}
              className={days === range ? "active" : ""}
              onClick={() => setDays(range)}
            >
              {range === 1 ? t("mgmt.mail.today") : t(`mgmt.mail.${range}days`)}
            </button>
          ))}
        </div>
        <button type="button" className="emailSystemRefresh" onClick={load} disabled={loading}>
          ↻ {t("mgmt.mail.refresh")}
        </button>
      </div>

      {error && <div className="profilError">{error}</div>}

      <div className={`emailProviderStatus ${circuitOpen ? "fallback" : "healthy"}`}>
        <span className="emailProviderDot" />
        <strong>{t("mgmt.mail.activeProvider")}: {metrics?.effectiveProvider || (circuitOpen ? "resend" : primaryProvider)}</strong>
        <small>{circuitOpen ? t("mgmt.mail.circuitOpen") : t("mgmt.mail.gatewayHealthy")}</small>
      </div>

      <div className="emailSystemCards" aria-busy={loading}>
        <article><span>{t("mgmt.mail.sentToday")}</span><strong>{Number(today.total || 0)}</strong></article>
        <article><span>{t("mgmt.mail.rangeTotal")}</span><strong>{Number(metrics?.totals?.total || 0)}</strong><small>{days} {t("mgmt.mail.dayUnit")}</small></article>
        <article><span>Brevo</span><strong>{Number(today.brevo || 0)}</strong><small>{brevoRemaining} {t("mgmt.mail.remaining")}</small></article>
        <article><span>Resend</span><strong>{Number(today.resend || 0)}</strong><small>{resendRemaining} {t("mgmt.mail.remaining")}</small></article>
        <article><span>{t("mgmt.mail.fallback")}</span><strong>{Number(today.fallback || 0)}</strong></article>
        <article className={failedAttempts > 0 ? "warning" : ""}>
          <span>{t("mgmt.mail.failedAttempts")}</span>
          <strong>{failedAttempts}</strong>
          <small>{undeliveredFailures} {t("mgmt.mail.undeliveredShort")}</small>
        </article>
      </div>

      <section className="emailSystemChartCard">
        <header>
          <div>
            <strong>{t("mgmt.mail.chartTitle")}</strong>
            <small>{t("mgmt.mail.chartHint")}</small>
          </div>
          <div className="emailSystemLegend"><span className="brevo">Brevo</span><span className="resend">Resend</span></div>
        </header>
        <div className="emailSystemDonutWrap">
          <div className="emailSystemDonutShell">
            <svg className="emailSystemDonut" viewBox="0 0 42 42" role="img" aria-label={t("mgmt.mail.chartTitle")}>
              <circle className="emailSystemDonutTrack" cx="21" cy="21" r="15.9155" />
              {chart.slices.filter((slice) => slice.value > 0).map((slice) => (
                <circle
                  key={slice.key}
                  className={`emailSystemDonutSlice ${selectedProvider === slice.key ? "selected" : ""}`}
                  cx="21"
                  cy="21"
                  r="15.9155"
                  pathLength="100"
                  stroke={slice.color}
                  strokeDasharray={`${slice.percent} ${100 - slice.percent}`}
                  strokeDashoffset={-slice.offset}
                  tabIndex="0"
                  role="button"
                  aria-label={`${slice.label}: ${slice.value}`}
                  onClick={() => setSelectedProvider((current) => current === slice.key ? null : slice.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedProvider((current) => current === slice.key ? null : slice.key);
                    }
                  }}
                />
              ))}
            </svg>
            <div className="emailSystemDonutCenter"><strong>{chart.total}</strong><span>{t("mgmt.mail.accepted")}</span></div>
            {selectedSlice && <div className="emailSystemChartPopover" role="status">
              <strong>{selectedSlice.label}</strong>
              <span>{selectedSlice.value} · %{selectedSlice.percent.toFixed(1)}</span>
            </div>}
          </div>
          <div className="emailSystemDonutDetails">
            {chart.slices.map((slice) => <button type="button" key={slice.key} onClick={() => setSelectedProvider(slice.key)} className={selectedProvider === slice.key ? "active" : ""}>
              <i style={{ background: slice.color }} />
              <span>{slice.label}</span>
              <strong>{slice.value}</strong>
              <small>%{slice.percent.toFixed(1)}</small>
            </button>)}
          </div>
        </div>
        {(metrics?.series || []).length > 0 && <div className="emailSystemPeriod">
          {formatDay(metrics.series[0].day, lang)} – {formatDay(metrics.series[metrics.series.length - 1].day, lang)}
        </div>}
      </section>

      {failedAttempts > 0 && <section className="emailFailureSummary">
        <div>
          <strong>{t("mgmt.mail.failureHeading")}</strong>
          <p>{t("mgmt.mail.failureHint")}</p>
        </div>
        <div className="emailFailureOutcomes">
          <span><b>{recoveredFailures}</b> {t("mgmt.mail.recovered")}</span>
          <span className={undeliveredFailures > 0 ? "danger" : ""}><b>{undeliveredFailures}</b> {t("mgmt.mail.undelivered")}</span>
        </div>
        {(metrics?.failures?.breakdown || []).length > 0 && <div className="emailFailureRows">
          {metrics.failures.breakdown.map((item) => <div key={`${item.category}:${item.provider}:${item.errorCode}`}>
            <span>{failureCategory(item.category)} · {item.provider}</span>
            <code>{item.errorCode}</code>
            <strong>{item.count}</strong>
          </div>)}
        </div>}
      </section>}

      <section className="emailSystemTestCard">
        <h4>{t("mgmt.mail.testHeading")}</h4>
        <label className="darkLabel" htmlFor="admin-email-test-target">{t("mgmt.mail.targetLabel")}</label>
        <input
          id="admin-email-test-target"
          className="darkInput"
          type="email"
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          placeholder={defaultEmail || t("mgmt.mail.targetPh")}
        />
        <p className="ayarAciklama">{t("mgmt.mail.targetHint")}</p>
        <button type="button" className="primaryButton" onClick={sendTest} disabled={sending}>
          {sending ? t("mgmt.mail.testSending") : t("mgmt.mail.testBtn")}
        </button>
        {testResult && (
          <div className={testResult.ok ? "profilSuccess" : "profilError"}>
            {testResult.ok ? t("mgmt.mail.testOk") : `${t("mgmt.mail.testFailed")}: ${testResult.message || ""}`}
            {testResult.ok && testResult.provider ? ` · ${testResult.provider}${testResult.fallbackUsed ? ` (${t("mgmt.mail.fallback")})` : ""}` : ""}
          </div>
        )}
      </section>
    </div>
  );
}
